import {
    calculateLineTotal,
    calculateExclusive,
    calculateExclusiveUnitPrice,
    calculateMarkup,
    calculatePriceFromMarkup,
} from '@/utils/line-totals'

/**
 * Pure per-field maths for editing a quote/invoice line item.
 * Returns the new item; merging it into the list is the caller's job.
 * `costing` enables the cost/markup fields (quotes); invoices pass false.
 */
export function applyLineItemFieldChange(item: any, key: string, value: any, options: { costing: boolean }): any {
    const { costing } = options
    const newValue = value

    if (key === 'Description' || key === 'InventoryDescription') {
        return { ...item, Description: newValue, InventoryDescription: newValue }
    }

    if (
        key === 'Quantity' || key === 'TaxPercentage' || key === 'LineDiscountPercentage' || key === 'UnitPriceExclusive' ||
        (costing && (key === 'UnitCostPrice' || key === 'UnitPriceMarkup'))
    ) {
        const newItem = {
            ...item,
            [key]: newValue
        }
        if (newItem.LineDiscountPercentage < 0 || newItem.LineDiscountPercentage > 100) {
            newItem.LineDiscountPercentage = newItem.LineDiscountPercentage < 0 ? 0 : 100
        }
        if (newItem.Quantity > 0) {
            if (costing) {
                if (key === 'UnitPriceMarkup') {
                    if (newValue !== '') {
                        if (!+item.UnitCostPrice) {
                            newItem.UnitPriceMarkup = item.UnitPriceMarkup === '' ? null : '' // needs to alternate to update input internal state with value change
                        }

                        if (+newItem.UnitCostPrice) {
                            newItem.UnitPriceExclusive = calculatePriceFromMarkup(newItem.UnitCostPrice, newValue)
                        }
                    }
                }

                if (key === 'UnitCostPrice') {
                    if (!+newValue) {
                        newItem.UnitPriceMarkup = ''
                    } else if (!item.UnitCostPrice || item.UnitPriceMarkup === '') { // previous margin is undefined
                        newItem.UnitPriceMarkup = calculateMarkup(item.UnitPriceExclusive, newValue)
                    } else if (item.UnitPriceExclusive && item.UnitCostPrice) {
                        const markup = calculateMarkup(item.UnitPriceExclusive, item.UnitCostPrice)
                        newItem.UnitPriceExclusive = calculatePriceFromMarkup(newValue, markup)
                    } else if (!item.UnitCostPrice || item.UnitCostPrice === 0) {
                        newItem.UnitPriceExclusive = newValue
                    }
                }

                if (key === 'UnitPriceExclusive') {
                    const buyPrice = item.UnitCostPrice ?? +(item.Inventory?.CostPrice ?? 0)
                    if (newValue !== '' && buyPrice) {
                        newItem.UnitPriceMarkup = calculateMarkup(newValue, buyPrice)
                    }
                }
            }

            newItem.LineTotalExclusive = calculateLineTotal({
                quantity: newItem.Quantity,
                unitPrice: newItem.UnitPriceExclusive,
                discountPercentage: newItem.LineDiscountPercentage,
            })
        } else {
            newItem.LineTotalExclusive = 0
        }
        return newItem
    }

    if (key === 'UnitPriceInclusive') {
        // Extract tax percentage from the item
        const taxPercentage = +item.TaxPercentage || 0

        // Create a new item with updated values
        const newItem = {
            ...item,
            UnitPriceExclusive: calculateExclusiveUnitPrice(newValue, taxPercentage)
        }

        // Handle markup calculation, similar to the UnitPriceExclusive calculation
        if (costing) {
            const buyPrice = item.UnitCostPrice ?? +(item.Inventory?.CostPrice ?? 0)
            if (newValue !== '' && buyPrice) {
                newItem.UnitPriceMarkup = calculateMarkup(newItem.UnitPriceExclusive, buyPrice)
            }
        }

        // The INCLUSIVE price is the one that was typed, so the line total is
        // calculated on that side and the exclusive amount taken as the remainder.
        // Deriving it from the exclusive unit price instead loses a cent - at 15%
        // an entered R80.00 would come back as R80.01.
        if (newItem.Quantity > 0) {
            const lineTotalInclusive = calculateLineTotal({
                quantity: newItem.Quantity,
                unitPrice: newValue,
                discountPercentage: newItem.LineDiscountPercentage,
            })
            newItem.LineTotalExclusive = calculateExclusive(lineTotalInclusive, taxPercentage)
        } else {
            newItem.LineTotalExclusive = 0
        }
        return newItem
    }

    return { ...item, [key]: newValue }
}
